import { useEffect, useRef, useState } from "react";
import * as d3 from "d3";
import * as topojson from "topojson-client";
import { Popper } from "@mui/material";


export default function ATSCCountyMap({ dmaData, dmaMapData, countyMapData, setMarketData}) {
    const svgRef = useRef();
    const [tooltipMarketTitle, setTooltipMarketTitle] = useState("");
    const [tooltipTVPerc, setTooltipTVPerc] = useState("");
    const [tooltipCablePerc, setTooltipCablePerc] = useState("");
    const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
    const [tooltipOpen, setTooltipOpen] = useState(false);
    const [tooltipType, setTooltipType] = useState("");

    

    // Use useEffect to draw the map when the data is loaded
    useEffect(() => {
        // Check if the map json is loaded
        if (!dmaMapData) return;

        // Clear the SVG before drawing
        d3.select(svgRef.current).selectAll("*").remove();
        const width = 975;
        const height = 610;

        // Create the SVG element
        const svg = d3.select(svgRef.current)
            .attr("viewBox", [0, 0, width, height])
            .attr("width", width)
            .attr("height", height)
            .attr("style", "max-width: 100%; height: auto; cursor: pointer;")
            .on("click", function (event) {
                if (event.target.tagName === "svg") {
                    resetZoom();
                }
            });


        // Create a group element to hold the map
        const g = svg.append("g");

        const projection = d3.geoAlbersUsa().scale(1300).translate([487.5, 305]);
        const path = d3.geoPath(projection);

        const wiggleRoom = 50;
        const bounds = [[-wiggleRoom, -wiggleRoom], [wiggleRoom + width, wiggleRoom + height]];

        const zoom = d3.zoom()
            .scaleExtent([1, 8])
            .translateExtent(bounds)
            .on("zoom", (event) => {
                requestAnimationFrame(() => {
                    g.attr("transform", event.transform);
                    g.attr("stroke-width", 1 / event.transform.k);

                    // Fade in counties when zoomed in.
                    if (event.transform.k > 3) {
                        g.select(".counties")
                            .transition()
                            .duration(250)
                            .style("opacity", 1)
                            .attr("visibility", "visible");
                    } else {
                        g.select(".counties")
                            .transition()
                            .duration(250)
                            .style("opacity", 0)
                            .on("end", function () {
                                d3.select(this).attr("visibility", "hidden");
                            });
                    }
                });
            });

        svg.call(zoom);


    // Zoom to feature function
    let focusedMarket = null;
    function zoomToFeature(feature) {
        if(focusedMarket === feature) {
            resetZoom();
            focusedMarket = null;
            return;
        }
        focusedMarket = feature;
        const [[x0, y0], [x1, y1]] = path.bounds(feature);
        const dx = x1 - x0;
        const dy = y1 - y0;
        const x = (x0 + x1) / 2;
        const y = (y0 + y1) / 2;
        const scale = Math.max(1, Math.min(8, 0.9 / Math.max(dx / width, dy / height)));
        const translate = [width / 2 - scale * x, height / 2 - scale * y];

        svg.transition()
            .duration(750)
            .call(
                zoom.transform,
                d3.zoomIdentity.translate(translate[0], translate[1]).scale(scale)
            );
        
    }

        const DMA = topojson.feature(dmaMapData, dmaMapData.objects.nielsen_dma).features;
        let dmaNames = [];

        g.append("g")
            .attr("class", "dma")
            .selectAll("path")
            .data(DMA)
            .join("path")
            .attr("fill", "#444")
            .attr("cursor", "pointer")
            .attr("stroke", "white")
            .attr("d", path)
            .on("mouseover", (event, d) => {
                setTooltipMarketTitle(d.properties.dma1);
                setTooltipTVPerc(`${d.properties.tvperc}`); 
                setTooltipCablePerc(`${d.properties.cableperc}`);
                setTooltipPosition({ x: event.pageX + 15, y: event.pageY + 15 }); 
                setTooltipOpen(true);
                setTooltipType("DMA");
            })
            .on("mousemove", (event) => {
                setTooltipPosition({ x: event.pageX + 15, y: event.pageY + 15 }); 
            })
            .on("mouseout", () => {
                setTooltipOpen(false); 
            })
            .on("click", (event, d) => { zoomToFeature(d); });

            DMA.forEach(element => {
                dmaNames.push(element.properties.dma1)
            });

            const counties = topojson.feature(countyMapData, countyMapData.objects.counties).features;
             g.append("g")
            .attr("class", "counties")
            .selectAll("path")
            .data(counties)
            .join("path")
            .attr("fill", "transparent")
            .attr("cursor", "pointer")
            .attr("stroke", "white")
            .attr("d", path)
            .on("mouseover", (event, d) => {
                setTooltipMarketTitle(`${d.properties.NAME}`); 
                setTooltipCablePerc(`${d.properties.COUNTYFP}`);
                setTooltipPosition({ x: event.pageX + 15, y: event.pageY + 15 }); 
                setTooltipOpen(true);
                setTooltipType("FIPS");
            })
            .on("mousemove", (event) => {
                setTooltipPosition({ x: event.pageX + 15, y: event.pageY + 15 }); 
            })
            .on("mouseout", () => {
                setTooltipOpen(false); 
            })
            .on("click", (event, d) => { zoomToFeature(d); });



        g.select(".counties").attr("visibility", "hidden");


        function resetZoom() {
            svg.transition().duration(750).call(
                zoom.transform,
                d3.zoomIdentity,
                d3.zoomTransform(svg.node()).invert([width / 2, height / 2])
            );
        }

    }, [dmaMapData, countyMapData]);

    return (
        <>
            <svg ref={svgRef}></svg>
            <Popper
                open={tooltipOpen}
                style={{
                    position: "absolute",
                    top: tooltipPosition.y,
                    left: tooltipPosition.x,
                    pointerEvents: "none",
                    
                }}
            >
                <div
                    style={{
                        background: "white",
                        border: "1px solid #ccc",
                        padding: "5px",
                        color: "black",
                        maxWidth: "300px",
                        boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
                    }}
                    >
                        <strong>{ tooltipMarketTitle }</strong>
                        <div style={{ marginTop: "5px" }}>
                            {tooltipType === "DMA" && (
                                <>
                                    <strong>TV Households:</strong> { tooltipTVPerc }% <br></br>
                                    <strong>Cable Households</strong> { tooltipCablePerc }%
                                </>
                            )}

                            {tooltipType === "FIPS" && (
                                <>
                                    <strong>County FIP:</strong> { tooltipCablePerc } <br></br>
                                </>
                            )}
                        </div>
                    </div>
                    
            </Popper>
        </>
    );
}